import { useContext } from 'react';
import { Flag, Plus, FolderPlus, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Switch from '@/components/switch/Switch';
import { LangContext } from '@/context/LangProvider';
import { QuestionGroupLimits } from '@/enums';

interface DiscussionGuideHeaderProps {
  showGroupUI: boolean;
  hasQuestions: boolean;
  isRootRandomized: boolean;
  isAddingQuestionsDisabled: boolean;
  isDisabled: boolean;
  isCreatingGroupDisabled: boolean;
  currentQuestionCount: number;
  // TELV1-421 — per-project-type question cap (embed = 3, interview = 40).
  // Defaults to the global cap so interview behaviour is unchanged.
  maxQuestions?: number;
  isEmbedProject?: boolean;
  isAddingCheckpointsDisabled?: boolean;
  onAddQuestion: () => void;
  onCreateGroup: () => void;
  onGlobalRandomizeToggle: (enabled: boolean) => void;
  onAddCheckpoint?: () => void;
}

export function DiscussionGuideHeader({
  showGroupUI,
  hasQuestions,
  isRootRandomized,
  isAddingQuestionsDisabled,
  isDisabled,
  isCreatingGroupDisabled,
  currentQuestionCount,
  maxQuestions = QuestionGroupLimits.MAX_QUESTIONS,
  isEmbedProject = false,
  isAddingCheckpointsDisabled = false,
  onAddQuestion,
  onCreateGroup,
  onGlobalRandomizeToggle,
  onAddCheckpoint,
}: DiscussionGuideHeaderProps) {
  const { lang } = useContext(LangContext);

  // TELV1-457 — the global question-limit warning is interview-only. Embeds are
  // capped per block (≤3) and on block count (≤10), surfaced by the dedicated
  // embed structure counter below the header, not by this flat-total warning.
  const hasReachedQuestionLimit = !isEmbedProject && currentQuestionCount >= maxQuestions;

  return (
    <>
      {/* Header with title */}
      <p className="flex items-center gap-1 text-[15px] font-medium">
        {lang.get('msg.interviewQuestions')}
      </p>

      {/* Action buttons and randomization toggle below title */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex gap-2">
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={onAddQuestion}
            disabled={isAddingQuestionsDisabled}
            className="flex items-center gap-1.5 bg-primary hover:bg-darker-primary text-white"
          >
            <Plus size={14} />
            {lang.get('msg.addQuestion')}
          </Button>
          {/* TELV1-421 — embeds are flat (checkpoints, no groups); hide grouping. */}
          {!isEmbedProject && (
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={onCreateGroup}
              disabled={isCreatingGroupDisabled}
              className="flex items-center gap-1.5 bg-tellet-pink hover:bg-medium-pink text-white"
            >
              <FolderPlus size={14} />
              {lang.get('msg.createGroup')}
            </Button>
          )}
          {isEmbedProject && onAddCheckpoint && (
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={onAddCheckpoint}
              disabled={isAddingCheckpointsDisabled}
              className="flex items-center gap-1.5 bg-embed hover:bg-embed-hover text-white"
            >
              <Flag size={14} />
              {lang.get('msg.addCheckpoint')}
            </Button>
          )}
        </div>

        {/* Global randomization toggle - show when there are questions or groups */}
        {hasQuestions && (
          <div className="flex items-center gap-2 ml-2">
            <Switch
              value={isRootRandomized}
              func={() => onGlobalRandomizeToggle(!isRootRandomized)}
              isDisabled={isDisabled}
            />
            <label className="text-sm text-gray-700">
              {showGroupUI ? lang.get('msg.randomizeGroups') : lang.get('msg.randomizeQuestions')}
            </label>
          </div>
        )}
      </div>

      {/* Warning message when question limit is reached */}
      {hasReachedQuestionLimit && !isDisabled && (
        <div className="flex items-center gap-2 mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md">
          <AlertCircle size={18} className="text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-800">
            {lang.get('msg.maxQuestionsLimitReached', {
              limit: String(maxQuestions),
            })}
          </p>
        </div>
      )}
    </>
  );
}
